import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { sendEvent } from '../../shared/search-event';
import WireResultsContext, { reducer, defaultState } from './wire-results-components/context';
import TitleMatch from './wire-results-components/title-match';
import SimilarTo from './wire-results-components/similar-to';
import LogoSeparator from '../shared/logo-separator';
import OtherWires from './wire-results-components/other-wires';
import TopKeywords from './wire-results-components/top-keywords';
import TopStockCodes from './wire-results-components/top-stockcodes';
import theme from '../../../assets/stylesheets/_settings.global.scss';

const Columnar = styled.div`
  padding-top: ${props => (props.theme.isDesktop ? 49 : 25)}px;
`;

export default function SearchResultsWire({ query, titleMatchedWires, searchUpdateEnabled }) {
  const isMobile = useMediaQuery({ query: `(max-width: ${theme.breakpointMediumMax})` });
  const isDesktop = useMediaQuery({ query: `(min-width: ${theme.breakpointLargeMax})` });
  const [st, dispatch] = React.useReducer(reducer, defaultState);
  const titleMatchedWireIDs = _.map(titleMatchedWires, 'id');

  React.useEffect(() => {
    (async () => sendEvent())();
  }, []);

  const className = isDesktop || isMobile ? 'columnar' : 'columnar no-sidebar';

  return (
    <ThemeProvider theme={{ ...theme, isDesktop }}>
      <WireResultsContext.Provider value={{ state: st, dispatch }}>
        <React.Fragment>
          <Columnar className={className}>
            <section className="content">
              <TitleMatch query={query} wires={titleMatchedWires} />
              {!!titleMatchedWires.length && (
                <LogoSeparator animate={st.loadingSimilar === 'init'} />
              )}
              {!isDesktop && (
                <React.Fragment>
                  <OtherWires titleMatchedWires={titleMatchedWires} />
                  <TopKeywords titleMatchedWires={titleMatchedWires} />
                  <TopStockCodes titleMatchedWires={titleMatchedWires} />
                  {!!(st.otherWires.length + st.keywords.length + st.stockCodes.length) && (
                    <LogoSeparator />
                  )}
                </React.Fragment>
              )}
              <SimilarTo
                query={query}
                exclude={titleMatchedWireIDs}
                searchUpdateEnabled={searchUpdateEnabled}
              />
              <LogoSeparator animate={st.loadingSimilar === 'more'} />
            </section>
            {isDesktop && (
              <section className="sidebar">
                <OtherWires titleMatchedWires={titleMatchedWires} />
                <TopKeywords titleMatchedWires={titleMatchedWires} />
                <TopStockCodes titleMatchedWires={titleMatchedWires} />
              </section>
            )}
          </Columnar>
        </React.Fragment>
      </WireResultsContext.Provider>
    </ThemeProvider>
  );
}

SearchResultsWire.propTypes = {
  query: PropTypes.string.isRequired,
  titleMatchedWires: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    firstPublishedAt: PropTypes.string.isRequired,
    isSponsored: PropTypes.bool.isRequired,
    commentCount: PropTypes.number.isRequired,
    likeCount: PropTypes.number.isRequired,
    liked: PropTypes.bool.isRequired,
    featuredCount: PropTypes.number.isRequired,
    userId: PropTypes.number.isRequired,
    editorsPicked: PropTypes.bool.isRequired,
    ownedByCurrentUser: PropTypes.bool.isRequired,
    category: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    series: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
    }).isRequired,
  })).isRequired,
  searchUpdateEnabled: PropTypes.bool.isRequired,
};
